import { Event } from "event-system";
import { DeckModel } from "../deck/DeckModel";
import { ICellModel } from "../cell/ICellModel";
import { IFigureModel } from "../figure/IFigureModel";
import { IFigureModelFactory } from "../../factories/figures/IFigureModelFactory";
import { ICellModelFactory } from "../../factories/cells/ICellModelFactory";
import { Bindable } from "bindable-data";
import { inject, injectable } from "@launchtray/tsyringe-async";
import { ISquareModel } from "../square/ISquareModel";
import { CellModelStatus } from "../../enums/CellModelStatus";
import { PlayerModel } from "../player/PlayerModel";
import { InjectableModelFactories } from "../../../../composition/InjectableModelFactories";

@injectable()
export class GameModel extends Bindable {
    private readonly gamefield: Array<ICellModel>;
    private readonly width: number;
    private readonly height: number;
    public readonly onChanged: Event;
    public readonly onReject: Event;
    public readonly onGameOver: Event<boolean>;
    public readonly onCanUseFigure: Event<number, boolean>;

    public constructor(
        @inject(InjectableModelFactories.ICellModelFactory) private readonly cellFactory: ICellModelFactory,
        @inject(InjectableModelFactories.IFigureModelFactory) private readonly figureFactory: IFigureModelFactory,
        public readonly deckModel: DeckModel,
        private readonly playerModel: PlayerModel,
    ) {
        super();
        this.width = 8;
        this.height = 12;
        this.onChanged = new Event();
        this.onReject = new Event();
        this.onGameOver = new Event<boolean>();
        this.onCanUseFigure = new Event<number, boolean>();
        this.gamefield = new Array<ICellModel>(this.width * this.height);
        for (let i = 0; i < this.gamefield.length; i++) {
            this.gamefield[i] = this.cellFactory.createCell();
        }
        this.deckModel.onChanged.add(this.tryEndGame, this);
    }

    private tryEndGame(): void {
        for (let cell of this.gamefield) {
            if (cell.Status != CellModelStatus.Empty) {
                return;
            }
        }
        this.onGameOver.emit(false);
    }

    private popSquares(): void {
        this.playerModel.Score++;
    }

    private canPlaceFigure(figure: IFigureModel, cellIndex: number): boolean {
        if (figure.isUltimate) {
            return true;
        }
        if (Array.isArray(figure.Shape)) {
            const shape = figure.Shape as Array<ISquareModel>;
            const startRow = Math.floor(cellIndex / this.width);
            const startCol = cellIndex % this.width;
            if (startCol + figure.FigureData.width > this.width) {
                return false;
            }
            if (startRow + figure.FigureData.height > this.height) {
                return false;
            }
            for (let row = 0, shapeIndex = 0; row < figure.FigureData.height; row++) {
                for (let col = 0; col < figure.FigureData.width; col++) {
                    if (!this.gamefield[(startRow + row) * this.width + (startCol + col)].tryCombine(shape[shapeIndex])) {
                        return false;
                    }
                    shapeIndex++;
                }
            }
            return true;
        }
        return this.gamefield[cellIndex].tryCombine(figure.Shape as ISquareModel);
    }

    public placeFigure(cellIndex: number): boolean {
        let placedSuccessfully = true;
        const figure = this.deckModel.deck[this.deckModel.SelectedFigureIndex];
        if (figure == null) {
            return;
        }
        if (cellIndex < 0 || cellIndex > this.gamefield.length) {
            this.onReject.emit();
            return false;
        }
        if (Array.isArray(figure.Shape)) {
            placedSuccessfully = this.placeMultipleCells(figure, cellIndex);
        } else {
            placedSuccessfully = this.placeSoloCell(figure, cellIndex);
        }
        if (!placedSuccessfully) {
            this.onReject.emit();
            return false;
        }
        this.popSquares();
        this.onChanged.emit();
        this.tryEndGame();
        return true;
    }

    public selectFigure(deckIndex: number): void {
        this.deckModel.SelectedFigureIndex = deckIndex;
    }

    private placeSoloCell(figure: IFigureModel, cellIndex: number): boolean {
        if (!figure.isUltimate) {
            if (!this.gamefield[cellIndex].tryCombine(figure.Shape as ISquareModel)) {
                return false;
            }
            this.gamefield[cellIndex].FigureInside = (figure.Shape as ISquareModel);
        } else {
            // this.gamefield[cellIndex].FigureInsideForced = (figure.Shape as ISquareModel);
            this.gamefield[cellIndex].eraseByBooster();
        }
        this.deckModel.removeSelectedFigureFromDeck();
        // this.onChanged.emit();
        return true;
    }

    private placeMultipleCells(figure: IFigureModel, cellIndex: number): boolean {
        const shape = figure.Shape as Array<ISquareModel>;
        const startRow = Math.floor(cellIndex / this.width);
        const startCol = cellIndex % this.width;
        if (startCol + figure.FigureData.width > this.width) {
            return false;
        }
        if (startRow + figure.FigureData.height > this.height) {
            return false;
        }
        if (!figure.isUltimate) {
            for (let row = 0, shapeIndex = 0; row < figure.FigureData.height; row++) {
                for (let col = 0; col < figure.FigureData.width; col++) {
                    if (!this.gamefield[(startRow + row) * this.width + (startCol + col)].tryCombine(shape[shapeIndex])) {
                        return false;
                    }
                    shapeIndex++;
                }
            }
            for (let row = 0, shapeIndex = 0; row < figure.FigureData.height; row++) {
                for (let col = 0; col < figure.FigureData.width; col++) {
                    this.gamefield[(startRow + row) * this.width + (startCol + col)].FigureInside = shape[shapeIndex]
                    shapeIndex++;
                }
            }
        } else {
            for (let row = 0, shapeIndex = 0; row < figure.FigureData.height; row++) {
                for (let col = 0; col < figure.FigureData.width; col++) {
                    this.gamefield[(startRow + row) * this.width + (startCol + col)].FigureInsideForced = shape[shapeIndex]
                    shapeIndex++;
                }
            }
        }
        this.deckModel.removeSelectedFigureFromDeck();
        // this.onChanged.emit();
        return true;
    }

    public countFieldCoverage(): number {
        const trianglesCount = this.Gamefield.reduce((acc, curr) => {
            return acc + curr.FigureInside.Triangles.length;
        }, 0);
        const totalCount = this.width * this.height * 2;
        return 100 - ((trianglesCount * 100) / totalCount);
    }

    public removeRandomTriangles(count: number = 3): void {
        const triangles = this.gamefield.map((cell, index) => {
            return [
                { cellIndex: index, triangleIndex: 0, triangle: cell.FigureInside.Triangles[0] },
                { cellIndex: index, triangleIndex: 1, triangle: cell.FigureInside.Triangles[1] }
            ];
        }).flat().filter(data => data.triangle != null);
        const elementsToRemove = triangles.random(count);
        if (Array.isArray(elementsToRemove)) {
            for (const element of elementsToRemove) {
                this.gamefield[element.cellIndex].eraseTriangleByIndex(element.triangleIndex);
            }
        } else {
            this.gamefield[elementsToRemove.cellIndex].eraseTriangleByIndex(elementsToRemove.triangleIndex);
        }
        this.popSquares();
        this.onChanged.emit();
        this.tryEndGame();
    }

    public clearGamefield(): void {
        for (let i = 0; i < this.gamefield.length; i++) {
            this.gamefield[i] = this.cellFactory.createCell();
        }
        this.tryEndGame();
        this.onChanged.emit();
    }

    public get Gamefield(): Array<ICellModel> {
        return this.gamefield;
    }

    public get GamefieldWidth(): number {
        return this.width;
    }

    public get GamefieldHeight(): number {
        return this.height;
    }
}